import { useMemo, useState } from 'react';
import { useAsyncData } from 'shared/hooks/useAsyncData';
import {
  ManualReservationInput,
  createManualReservation as createManualReservationRequest,
  fetchReservations,
  updateReservation as updateReservationRequest,
} from 'modules/arena/reservations/reservationsService';
import { Reservation, ReservationStatus } from 'modules/arena/reservations/types';

export type { ManualReservationInput } from 'modules/arena/reservations/reservationsService';

export function useArenaReservations() {
  const { data: reservations, isLoading, error, setData: setReservations } = useAsyncData<Reservation[]>(
    fetchReservations,
    []
  );
  const [statusFilter, setStatusFilter] = useState<ReservationStatus | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string>('');

  const filteredReservations = useMemo(
    () =>
      statusFilter === 'all'
        ? reservations
        : reservations.filter((reservation) => reservation.status === statusFilter),
    [reservations, statusFilter]
  );

  const selectedReservation =
    reservations.find((reservation) => reservation.id === selectedId) ?? reservations[0] ?? null;

  function updateReservation(id: string, patch: Partial<Reservation>) {
    setReservations((prev) =>
      prev.map((reservation) => (reservation.id === id ? { ...reservation, ...patch } : reservation))
    );
    updateReservationRequest(id, patch).catch(() => {
      // Projeto de apresentação: mantemos o otimismo local mesmo se a chamada falhar.
    });
  }

  function confirm(id: string) {
    updateReservation(id, { status: 'confirmed', expiresAtLabel: undefined });
  }

  function cancel(id: string) {
    updateReservation(id, { status: 'cancelled', expiresAtLabel: undefined });
  }

  function markNoShow(id: string) {
    updateReservation(id, { status: 'no_show' });
  }

  function markCompleted(id: string) {
    updateReservation(id, { status: 'completed' });
  }

  function extendDeadline(id: string) {
    const reservation = reservations.find((item) => item.id === id);
    if (!reservation) return;
    updateReservation(id, { expiresAtLabel: `${reservation.expiresAtLabel} (prorrogado +15min)` });
  }

  function chargeRemaining(id: string) {
    const reservation = reservations.find((item) => item.id === id);
    if (!reservation) return;

    const participants = reservation.participants.map((participant) => ({
      ...participant,
      amountPaid: participant.amountDue,
    }));

    updateReservation(id, { participants, status: 'confirmed', expiresAtLabel: undefined });
  }

  async function createManualReservation(input: ManualReservationInput) {
    const newReservation = await createManualReservationRequest(input);
    setReservations((prev) => [newReservation, ...prev]);
    setSelectedId(newReservation.id);
    return newReservation;
  }

  return {
    reservations: filteredReservations,
    allReservations: reservations,
    isLoading,
    error,
    statusFilter,
    setStatusFilter,
    selectedId,
    setSelectedId,
    selectedReservation,
    confirm,
    cancel,
    markNoShow,
    markCompleted,
    extendDeadline,
    chargeRemaining,
    createManualReservation,
  };
}
